/**
 * Пре-блюр обложек для «фейкового стекла» карточек (PopularModelCard).
 *
 * История вопроса: живой backdrop-filter на сетке карточек фризил скролл
 * (~50мс/кадр), его заменили статичной blur()-копией; но CSS-фильтр всё
 * равно пере-растеризуется GPU при каждом входе карточки в вьюпорт, а
 * изоляция слоёв (will-change) упёрлась в бюджет композитора Chrome, когда
 * карточек стало 100+ — на слабых машинах плитки приезжали пустыми
 * («контент прогружается повторно», скрин владельца 2026-08-13).
 *
 * Здесь блюр считается ОДИН раз в canvas, результат живёт обычной
 * картинкой: прокрутка её просто рисует, фильтров и промоушена слоёв не
 * нужно. Кэш по src на всю сессию, параллельные запросы одной обложки
 * склеиваются. Любая ошибка (CORS, декод, старый браузер) → null —
 * карточка остаётся на прежнем CSS-фильтре (graceful).
 */
const cache = new Map<string, Promise<string | null>>()

const BLUR_PX = 10
// Ширина растра ≈ ширине карточки (246px + кромки): радиус блюра в этих
// координатах совпадает с прежним filter: blur(10px) на элементе карточки.
const RASTER_W = 262

export function preBlurredCover(src: string): Promise<string | null> {
	let p = cache.get(src)
	if (!p) {
		p = render(src).catch(() => null)
		cache.set(src, p)
	}
	return p
}

async function render(src: string): Promise<string | null> {
	const img = new Image()
	img.crossOrigin = 'anonymous'
	img.decoding = 'async'
	const loaded = new Promise<void>((res, rej) => {
		img.onload = () => res()
		img.onerror = () => rej(new Error('cover load failed'))
	})
	img.src = src
	await loaded

	const w = RASTER_W
	const h = Math.max(1, Math.round((img.naturalHeight / Math.max(1, img.naturalWidth)) * w))
	const canvas = document.createElement('canvas')
	canvas.width = w
	canvas.height = h
	const ctx = canvas.getContext('2d')
	if (!ctx || typeof ctx.filter !== 'string') return null

	// Bleed на радиус блюра: рисуем чуть шире холста, чтобы фильтру было что
	// сэмплировать за краями — иначе по периметру остаётся тёмная кромка
	// (в CSS-версии её прятали отрицательные поля элемента).
	ctx.filter = `blur(${BLUR_PX}px)`
	ctx.drawImage(img, -BLUR_PX * 2, -BLUR_PX * 2, w + BLUR_PX * 4, h + BLUR_PX * 4)

	const blob = await new Promise<Blob | null>((res) => canvas.toBlob(res, 'image/jpeg', 0.82))
	if (!blob) return null
	return URL.createObjectURL(blob)
}
